import { createContext, useCallback, useState } from 'react'

import { api } from 'utils/api'
import { getError } from 'utils/helpers/getError'
import { useCustomContext } from 'utils/helpers/useCustomContext'
import { GenericResponse, GenericStateType } from 'utils/types'

import { PinChangeFormType, PinContextType, PinCreateFormType, PinResetFormType, PinStateType } from './pinTypes'

const INITIAL_PIN_CONTEXT: PinContextType = {
  setLoading: () => null,
  setError: () => null,
  clearError: () => null,
  changePin: () => new Promise(() => false),
  checkUserPin: () => new Promise(() => false),
  createPin: () => new Promise(() => false),
  resetPin: () => new Promise(() => false),
  pinState: {
    error: '',
    loading: false,
    state: { isHavePin: false },
  },
}

const PinContext = createContext<PinContextType>(INITIAL_PIN_CONTEXT)

const PinProvider: React.FC = ({ children }) => {
  const [pinState, setPinState] = useState<GenericStateType<PinStateType>>(INITIAL_PIN_CONTEXT.pinState)

  const setLoading = (value = true) =>
    setPinState(prevState => ({ ...prevState, loading: value }))

  const setError = (msg: string) =>
    setPinState(prevState => ({ ...prevState, loading: false, error: msg }))

  const clearError = () =>
    setPinState(prevState => ({ ...prevState, error: '' }))

  const checkUserPin = useCallback(async (skipError = false) => {
    try {
      setLoading(true)

      const { data: { payload } } = await api.get<GenericResponse<boolean>>('User/ExistsPin')

      setPinState(prevState => ({ ...prevState, state: { ...prevState.state, isHavePin: payload } }))
      setLoading(false)

      return payload
    } catch (err) {
      const message = getError(err)

      !skipError && setError(message)

      setPinState(prevState => ({ ...prevState, loading: false, state: { ...prevState.state, isHavePin: false } }))

      return false
    }
  }, [])

  const createPin = async (data: PinCreateFormType) => {
    try {
      setLoading()
      await api.post<GenericResponse<boolean>>('User/CreatePin', data)

      checkUserPin(true)
      setLoading(false)

      return true
    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  const changePin = async (data: PinChangeFormType) => {
    try {
      setLoading()
      await api.put<GenericResponse<boolean>>('User/UpdatePin', data)

      setLoading(false)

      return true
    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  const resetPin = async (data: PinResetFormType) => {
    try {
      setLoading()
      await api.post<GenericResponse<boolean>>('User/ResetPin', data)
      checkUserPin(true)
      setLoading(false)

      return true
    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  return (
    <PinContext.Provider
      value={{
        pinState,
        setLoading,
        setError,
        clearError,
        checkUserPin,
        createPin,
        changePin,
        resetPin,
      }}>
      {children}
    </PinContext.Provider>
  )
}

const usePin = () => useCustomContext<PinContextType>(PinContext)

export { PinProvider, usePin }
